import React, { useEffect, useState } from "react";
import { offmarketApi } from "../../lib/api";
import { toast } from "sonner";
import { X, Search, Database } from "lucide-react";

const RANGES = [["1,10", "1–10"], ["11,20", "11–20"], ["21,50", "21–50"], ["51,100", "51–100"], ["101,200", "101–200"]];

// Apollo company database search by keyword / location / headcount → save picks as targets.
export const ApolloSearchModal = ({ open, onClose, defaults, onSave }) => {
  const [kw, setKw] = useState("");
  const [loc, setLoc] = useState("");
  const [ranges, setRanges] = useState(["1,10", "11,20", "21,50", "51,100"]);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setKw(defaults?.keywords || ""); setLoc(defaults?.location || ""); setRows([]); setSel({}); setPage(1); } }, [open]); // eslint-disable-line
  if (!open) return null;

  const run = async (p = 1) => {
    setLoading(true);
    try {
      const r = await offmarketApi.apolloSearch({ keywords: kw.split(",").map((s) => s.trim()).filter(Boolean), location: loc, employeesRanges: ranges, page: p, perPage: 25 });
      setRows(r.companies || []); setPage(r.page || p); setPages(r.totalPages || 1); setTotal(r.total || 0); setSel({});
      if (!(r.companies || []).length) toast.info("Apollo found nothing for that combination — loosen the headcount or location");
    } catch (e) { toast.error(e?.response?.data?.detail || "Apollo search failed"); }
    finally { setLoading(false); }
  };
  const picked = rows.filter((r, i) => sel[i]);
  const save = async () => {
    if (!picked.length) return toast.error("Tick at least one company");
    setSaving(true);
    try { await onSave(picked); onClose(); } finally { setSaving(false); }
  };
  const toggleRange = (k) => setRanges((r) => (r.includes(k) ? r.filter((x) => x !== k) : [...r, k]));

  return (
    <div className="modal-overlay" data-testid="apollo-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 900, width: "96%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-x" onClick={onClose} aria-label="Close" data-testid="apollo-close"><X size={14} /></button>
        <div className="modal-title"><Database size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Apollo company search</div>
        <div className="modal-sub">Pull small businesses straight from Apollo's company database by trade keyword, metro and headcount. Save picks, then “Find owner” reveals the decision-maker's name, title, email and LinkedIn.</div>
        <div className="om-apollo-form">
          <input className="inp" value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Keywords, comma separated — landscaping, lawn care, irrigation" data-testid="apollo-keywords" />
          <input className="inp" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Dallas, Texas" style={{ maxWidth: 220 }} data-testid="apollo-location" />
          <button className="btn btn-primary" onClick={() => run(1)} disabled={loading} data-testid="apollo-run"><Search size={13} />{loading ? "Searching…" : "Search"}</button>
        </div>
        <div className="om-ranges">
          <span>Headcount</span>
          {RANGES.map(([k, l]) => <button key={k} className={`chip ${ranges.includes(k) ? "on" : ""}`} onClick={() => toggleRange(k)} data-testid={`apollo-range-${k.replace(",", "-")}`}>{l}</button>)}
        </div>
        {rows.length > 0 && (
          <>
            <div className="cm-empty" style={{ margin: "8px 0 4px" }}>{total.toLocaleString()} matches · page {page} of {pages} · {picked.length} selected</div>
            <table className="tbl" data-testid="apollo-results">
              <thead><tr><th style={{ width: 26 }}><input type="checkbox" checked={rows.length > 0 && rows.every((_, i) => sel[i])} onChange={() => { const all = rows.every((_, i) => sel[i]); const n = {}; if (!all) rows.forEach((_, i) => { n[i] = true; }); setSel(n); }} data-testid="apollo-all" /></th><th>Company</th><th>Location</th><th>Phone</th><th>Employees</th><th>Founded</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.apolloId || i} data-testid={`apollo-row-${i}`}>
                    <td><input type="checkbox" checked={!!sel[i]} onChange={() => setSel((s) => ({ ...s, [i]: !s[i] }))} data-testid={`apollo-check-${i}`} /></td>
                    <td><b>{r.company}</b>{r.website ? <div><a href={r.website} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "var(--lime)" }}>{r.website.replace(/^https?:\/\/(www\.)?/, "")}</a></div> : null}</td>
                    <td style={{ fontSize: 11 }}>{[r.city, r.state].filter(Boolean).join(", ") || r.address || <span style={{ color: "var(--text3)" }}>{loc || "—"}</span>}</td>
                    <td style={{ fontSize: 11 }}>{r.phone || "—"}</td>
                    <td style={{ fontSize: 11 }}>{r.employees ?? "—"}</td>
                    <td style={{ fontSize: 11 }}>{r.founded ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-actions" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn" onClick={() => run(page - 1)} disabled={loading || page <= 1} data-testid="apollo-prev">Prev</button>
                <button className="btn" onClick={() => run(page + 1)} disabled={loading || page >= pages} data-testid="apollo-next">Next page</button>
              </div>
              <button className="btn btn-primary" onClick={save} disabled={saving || !picked.length} data-testid="apollo-save">{saving ? "Saving…" : `Save ${picked.length} as targets`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
